/*
<summary>
   This functional component is all about rendering the Return Tool form for capturing the loan date and the actual return date.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Formik, Form as FormikForm } from 'formik';
import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { IToolLoanDetailsVM } from '../../../models/response/IToolManagementResponse';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikDateFieldMUI from '../../../shared-components/FormikFormHandlers/FormikDateFieldMUI';
import { ReturnToolValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import Label from '../../../shared-components/label/label';
import { BorrowerType } from '../../../constants/constants';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';

export interface IReturnToolFormValues {
  LoanDate: Date | string | null;
  ReturnDate: Date | string | null;
}

/**
* This function is used to detect an unset Planned Return Date ("0001-01-01T00:00:00...")
* @param value: Planned Return Date as returned by the API
*/
const isUnsetDate = (value: string | null | undefined): boolean =>
  !value || value.startsWith('0001-01-01');

/**
* This function is used to format Planned Return Date as yyyy/mm/dd, matching the Loan
* Date/Return Date fields on this same modal.
* @param value: Planned Return Date as returned by the API
*/
const formatPlannedReturnDate = (value: string): string => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
};

interface IProps {
  toolManagementNo: string;
  loanDetails: IToolLoanDetailsVM | undefined;
  initialValues: IReturnToolFormValues;
  isLoading: boolean;
  onModalClosed: () => void;
  submitHandler: (values: IReturnToolFormValues) => void;
}

const ReturnToolForm = (props: IProps) => {
  return (
    <Modal
      show={true}
      centered
      className="return-tool-modal"
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id="return_the_tool" /> - {props.toolManagementNo}
         <button
            onClick={props.onModalClosed}
            className="Crossicons"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={props.initialValues}
        enableReinitialize
        validationSchema={ReturnToolValidateSchema}
        validateOnBlur={false}
        onSubmit={props.submitHandler}
      >
        {() => (
          <FormikForm className="user-form">
             {props.isLoading && (
                <FormLoader
                  loading={props.isLoading}
                  contained
                />
              )}
            <Modal.Body>
              {props.loanDetails && (
                <div className="row mb-3">
                  <div className="col-6">
                    <div className="form-group mb-2">
                      <span className="label-style col-form-label d-block">
                        <FormattedMessage id="lender_factory" />
                      </span>
                      <span>{props.loanDetails.LenderFactoryName}</span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group mb-2">
                      <span className="label-style col-form-label d-block">
                        <FormattedMessage id="borrower_factory" />
                      </span>
                      <span>
                        {props.loanDetails.BorrowerType === BorrowerType.Subcontractor
                          ? props.loanDetails.SubcontractorName
                          : props.loanDetails.BorrowerFactoryName}
                      </span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group mb-0">
                      <span className="label-style col-form-label d-block">
                        <FormattedMessage id="loan_status" />
                      </span>
                      <span>{props.loanDetails.LoanStatus}</span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group mb-0">
                      <span className="label-style col-form-label d-block">
                        <FormattedMessage id="planned_return_date" />
                      </span>
                      <span>
                        {isUnsetDate(props.loanDetails.PlannedReturnDate) ? (
                          <FormattedMessage id="unconfirmed" />
                        ) : (
                          formatPlannedReturnDate(props.loanDetails.PlannedReturnDate as string)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-6">
                  <div className="form-group mb-0">
                    <Label
                      className="label-style col-form-label"
                      label="loan_date"
                    />
                    <Field
                      name="LoanDate"
                      disabled
                      as={FormikDateFieldMUI}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group mb-0">
                    <Label
                      className="label-style col-form-label"
                      required
                      label="return_date"
                    />
                    <Field
                      name="ReturnDate"
                      as={FormikDateFieldMUI}
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex justify-content-center w-100">
                <Button
                  variant="light-danger"
                  className="me-2"
                  type="button"
                  onClick={props.onModalClosed}
                  disabled={props.isLoading}
                >
                  <FormattedMessage id="cancel" />
                </Button>
                <LoaderButton
                  type="submit"
                  isLoading={props.isLoading}
                  text="return_tool"
                />
              </div>
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export default ReturnToolForm;
